import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  memo,
  useMemo,
} from 'react';
import { DynamicFieldType } from './DynamicFormField';
import { CoreFieldEnumOpt } from 'providers/contexts/extensions/DynamicFieldData';
import { useDebouncedValue } from '@mantine/hooks';

// Selected Values types
export type SelectedValuesType =
  | string
  | string[]
  | { items: string[]; include_children?: boolean }
  | null
  | undefined;

// Supports both direct value and react functional updates
export type TUpdateDynamicOptsFn = (
  value: CoreFieldEnumOpt[] | ((prev: CoreFieldEnumOpt[]) => CoreFieldEnumOpt[]),
) => void;

export type TUpdateFetchedOptsFn = (
  value: CoreFieldEnumOpt[] | ((prev: CoreFieldEnumOpt[]) => CoreFieldEnumOpt[]),
) => void;

/**
 * DynamicFieldContextType
 *
 * dynamicOptions: main source of data for options which
 * accepts optional initDynamicOpts and onDynamicOptsChange
 * callbacks to allow parent to update/manipulate options.
 * Chips and display items can read from this state.
 *
 * fetchedOptions: state is for options that are fetched
 * via the current denum api call and is flattened to array.
 * This can be a subset of dynamicOptions since both are updated
 * in the same effect when the api results come in. If neccesary
 * a transform cb can also be passed but is generally not needed.
 *
 * Two states is required for certain components to render only
 * the fetched options list, for example a typeahead dropdown
 * while selected chips and other display can read via dynamicOptions.
 *
 */
export type DynamicFieldContextType = {
  fieldConfig: DynamicFieldType;
  searchText: string;
  cleanDbSearchText: string; // debounced sanitized query text
  updateSearchText: (text: string) => void;
  selectedValues: SelectedValuesType;
  updateSelectedValues: (values: SelectedValuesType) => void;
  dynamicOptions: CoreFieldEnumOpt[]; // all denum opts
  updateDynamicOptions: TUpdateDynamicOptsFn;
  fetchedOptions: CoreFieldEnumOpt[]; // fetched denum opts
  updateFetchedOptions: TUpdateFetchedOptsFn;
  includeChildren: boolean;
  setIncludeChildren: (includeChildren: boolean) => void;
};

const DynamicFieldContext = createContext<DynamicFieldContextType | undefined>(undefined);

export const useDynamicFieldContext = () => {
  const context = useContext(DynamicFieldContext);
  if (!context) {
    throw new Error('useDynamicFieldContext must be used within a DynamicFieldProvider');
  }
  return context;
};

/**
 * DynamicFieldProviderProps
 *
 * initDynamicOpts:
 * Initial options in context before any state update.
 * Note: the fetch denum resolver fetch will update state.
 * For example editing a pre-exiting form item where the values
 * are known up front but not yet fetched via infinite query, so the
 * chips/label can render before page containing the value is loaded.
 * This case would likely pair with an onDynamicOptsChange callback,
 * adding in any 'missing' options that arent fetched yet but are
 * needed for the chips to render their value:label pairings.
 * This is not needed if the value:label are simple strings or identical.
 *
 * onDynamicOptsChange:
 * Optional callback when updateDynamicOptions is called.
 * Receives the updated options and returns the value to store.
 * If omitted updated opts replace state as-is (existing behavior).
 * This allows parents to intercept options and apply any sort of
 * transforms, merges, etc before set in state and provided.
 *
 * onFetchedOptsChange:
 * Similar optional callback like onDynamicOptsChange.
 * Generally just onDynamicOptsChange cb is sufficient.
 *
 */
export type DynamicFieldProviderProps = {
  children?: React.ReactNode;
  fieldConfig: DynamicFieldType;
  initDynamicOpts?: CoreFieldEnumOpt[];
  onDynamicOptsChange?: (options: CoreFieldEnumOpt[]) => CoreFieldEnumOpt[];
  onFetchedOptsChange?: (options: CoreFieldEnumOpt[]) => CoreFieldEnumOpt[];
};

export const DynamicFieldProvider = memo(
  ({
    children,
    fieldConfig,
    initDynamicOpts,
    onDynamicOptsChange,
    onFetchedOptsChange,
  }: DynamicFieldProviderProps) => {
    const [searchText, setSearchText] = useState('');

    const [selectedValues, setSelectedValues] = useState<SelectedValuesType>([]);
    // Denum options states
    const [dynamicOptions, setDynamicOptions] = useState<CoreFieldEnumOpt[]>(
      initDynamicOpts ?? [],
    );
    const [fetchedOptions, setFetchedOptions] = useState<CoreFieldEnumOpt[]>([]);
    const [includeChildren, setIncludeChildren] = useState<boolean>(false);

    // Debounced clean search text for API query usage
    const [dbSearchText] = useDebouncedValue(searchText, 500);

    const cleanDbSearchText = useMemo(() => {
      return dbSearchText.trim();
    }, [dbSearchText]);

    const updateSearchText = useCallback((text: string) => {
      setSearchText(text);
    }, []);

    const updateSelectedValues = useCallback((values: SelectedValuesType) => {
      setSelectedValues(values);
    }, []);

    // Update dynamic options state w/ optional callback support
    const updateDynamicOptions: TUpdateDynamicOptsFn = useCallback(
      (value) => {
        setDynamicOptions((prev) => {
          // Handle react functional state dispatch...
          const items = typeof value === 'function' ? value(prev) : value;
          // If callback fn is provided, return result as new state
          if (onDynamicOptsChange) {
            return onDynamicOptsChange(items);
          }

          return items;
        });
      },
      [onDynamicOptsChange],
    );

    // Update fetched options state w/ optional callback support
    // generally not needed to have a transform on fetched options
    // as onDynamicOptsChange is sufficent for most use cases.
    const updateFetchedOptions: TUpdateFetchedOptsFn = useCallback(
      (value) => {
        setFetchedOptions((prev) => {
          const items = typeof value === 'function' ? value(prev) : value;
          if (onFetchedOptsChange) {
            return onFetchedOptsChange(items);
          }
          return items;
        });
      },
      [onFetchedOptsChange],
    );

    const fieldContext: DynamicFieldContextType = useMemo(() => {
      return {
        fieldConfig,
        searchText,
        cleanDbSearchText,
        updateSearchText,
        selectedValues,
        updateSelectedValues,
        dynamicOptions,
        updateDynamicOptions,
        fetchedOptions,
        updateFetchedOptions,
        includeChildren,
        setIncludeChildren,
      };
    }, [
      cleanDbSearchText,
      dynamicOptions,
      fetchedOptions,
      fieldConfig,
      includeChildren,
      searchText,
      selectedValues,
      updateDynamicOptions,
      updateFetchedOptions,
      updateSearchText,
      updateSelectedValues,
    ]);

    return (
      <DynamicFieldContext.Provider value={fieldContext}>
        {children}
      </DynamicFieldContext.Provider>
    );
  },
);
DynamicFieldProvider.displayName = 'DynamicFieldProvider';
